import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { setContext } from '@apollo/client/link/context';
import { ApolloModule, APOLLO_OPTIONS } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { ApolloLink, InMemoryCache } from '@apollo/client/core';
import { environment } from 'src/environments/environment';
import { onError } from '@apollo/client/link/error';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { DeviceService } from 'src/app/shared/services/device.service';
import { AuthTokenService } from 'src/app/shared/services/auth-token.service';
import { StorageService } from '../services/storage.service';

/**
 * Creates apollo client and adds API key and Auth headers.
 *
 * @param httpLink - Apollo http link.
 * @param storage
 * @param router
 * @param apiInterface
 * @param deviceService
 * @param authTokenService - Service to fetch the access token.
 */
export function createApollo(
  httpLink: HttpLink,
  storage: StorageService,
  router: Router,
  deviceService: DeviceService,
  authTokenService: AuthTokenService,
) {
  const deviceType = deviceService.getDeviceType();

  const basic = setContext((operation, context) => ({
    headers: {
      'X-API-KEY': environment.apiKey,
      isWeb: 'false',
      deviceType,
      ...context.headers,
    },
  }));

  const auth = setContext((operation, context) => {
    const accessToken = authTokenService.accessToken;

    if (!accessToken) {
      return {};
    }

    return {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ...context.headers,
      },
    };
  });

  const target = setContext(async (operation, context) => {
    // make sure storage is ready
    await storage.ready();
    const targetUUID = await authTokenService.getTargetUUID();
    if (!targetUUID) {
      return {};
    }

    return {
      headers: {
        'Target-User-Uuid': targetUUID,
        ...context.headers,
      },
    };
  });

  // GraphQL error link
  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      // For now we are just logging the error.
      graphQLErrors.forEach(({ message }) => console.log(`[GraphQL error]: Message: ${message}`));
    }

    // Handle network errors
    if (networkError) {
      console.log(`[Network error]: ${networkError.message}`);

      // Redirect to maintenace page on 503
      if ((networkError as HttpErrorResponse).status === 503) {
        router.navigate(['/maintenance-downtime']);
      }
    }
  });

  const link = ApolloLink.from([
    errorLink,
    basic,
    auth,
    target,
    httpLink.create({ uri: `${environment.apiUrl()}/graphql` }),
  ]);
  const cache = new InMemoryCache({
    addTypename: false,
  });
  const defaultOptions = {
    watchQuery: {
      // disable caching
      fetchPolicy: 'no-cache',
      errorPolicy: 'ignore',
    },
    query: {
      fetchPolicy: 'no-cache',
      // This policy ensures that apollo client throws an error
      // if query fails and does not return any partial data.
      errorPolicy: 'none',
    },
    mutation: {
      // This policy ensures that apollo client throws an error
      // if query fails and does not return any partial data.
      errorPolicy: 'none',
    },
  };
  return {
    link,
    cache,
    defaultOptions,
  };
}

@NgModule({
  declarations: [],
  imports: [CommonModule, ApolloModule],
  providers: [
    {
      provide: APOLLO_OPTIONS,
      useFactory: createApollo,
      deps: [HttpLink, StorageService, Router, DeviceService, AuthTokenService],
    },
  ],
})
export class GraphqlModule {}
